const { pickBy, sumBy, mapValues, groupBy, sum, } = require('lodash');
const { format: formatDate, } = require('date-fns');

const { types: expenseTypes, } = require('./models/expense');
const { resolveUnitCostAtWithTax, netQuantityOf, shippingQuantityOf } = require('./unitCostHistory');
const { includeTax } = require('./util');

const { entries } = Object;

const rakutenPlanMonthlyFees = { ganbare: 27500, standard: 71500, mega: 143000 };
const { computeMonthlyFee: computeShopifyMonthlyFee } = require('./models/shopifyFeeRates');

const createPlItems = (data, config) => {
  const { allProductsSales, values, productsBySku, unitShippingCostsBySku, fbaStorageFees, amazonAdCosts, rakutenAdCosts } = data;
  const { sellers, shops, dateRangeType, computedExpenses, periodStart, monthDays, handleTax, isExcludeTax = false } = config;

  const salesValue = values?.sales || 0;
  const refundValue = values?.refund || 0;
  // 原価は売上docの日付ごとに有効原価(unitCostHistory)を引いて正味数量(返品・Vine控除後)を掛ける（productsBySku: sku|id → product）
  const productCostValue = -sum(allProductsSales.map(doc => sum(entries(doc.values || {}).map(([sku, salesData]) => resolveUnitCostAtWithTax(productsBySku?.[sku], doc.date, isExcludeTax) * netQuantityOf(salesData)))));
  const shippingCostValue = -sum(allProductsSales.map(_ => sum(entries(_.values || {}).map(([sku, salesData]) => (unitShippingCostsBySku?.[sku] ?? 0) * shippingQuantityOf(salesData)))));
  const importsCostValue = 0;
  const commissionValue = values?.commission || 0;
  const systemUsageFeeValue = values?.systemUsageFee || 0;
  const safetyFeeValue = values?.safetyFee || 0;
  const paymentFeeValue = values?.paymentFee || 0;
  const fbaFeeValue = values?.fbaFee || 0;
  // 楽天 RPP は rakutenProductsAdKpis.values（{ productId: kpis }）の cost を合算。商品損益と同じデータソース
  const rakutenAdCostValue = sum((rakutenAdCosts || []).map(_ => sumBy(Object.values(_.values || {}), kpi => kpi.cost || 0)));
  // 広告レポートの費用はもともと税抜のため、税抜き表示では割り戻さず、税込み表示で加算する
  const adCostValue = sumBy(amazonAdCosts, 'totalCost') + rakutenAdCostValue;
  const spAmountValue = -(isExcludeTax ? adCostValue : includeTax(adCostValue));

  const inventoryFeeValue = handleTax(-Math.round(
    sumBy(fbaStorageFees, 'values.estimated_monthly_storage_fee')
    / (dateRangeType === 'daily' ? monthDays : 1)
  ));

  const pointsBaseCostValue = values?.pointsBaseCost || 0;
  // Shopify の決済手数料。Shopify Payments の実額(取れる場合)またはプラン別料率での推定値
  const transactionFeeValue = values?.transactionFee || 0;
  const codsValue = productCostValue + importsCostValue;
  const grossProfitValue = salesValue + refundValue + codsValue;
  const cods2Value = commissionValue + systemUsageFeeValue + safetyFeeValue + paymentFeeValue + fbaFeeValue + shippingCostValue + pointsBaseCostValue + transactionFeeValue;
  const grossProfit2Value = grossProfitValue + cods2Value;
  const pointsCostValue = values?.pointsCost || 0;
  const promotionCostValue = (values?.promotionCost || 0) + pointsCostValue;
  const otherVariableCostValue = dateRangeType === 'monthly' ? -sum(Object.values(computedExpenses?.variable || {})) : 0;
  const variableSgaValue = spAmountValue + inventoryFeeValue + promotionCostValue + otherVariableCostValue;
  const contributionMarginValue = grossProfit2Value + variableSgaValue;
  const otherFixedCostValue = -sum(Object.values(computedExpenses?.fixed || {}));
  const isPastMonth = periodStart && formatDate(periodStart, 'yyyy-MM') <= formatDate(new Date(), 'yyyy-MM');
  const amazonPlatformFee = isPastMonth ? handleTax(-5390 * sellers.length) : 0;
  // モールごとに出店料を計算する。mall で絞らないと Shopify 店舗にも楽天の出店料がかかる
  const rakutenPlatformFee = isPastMonth ? handleTax(-sum((shops || []).filter(_ => _.mall === 'rakuten').map(_ => rakutenPlanMonthlyFees[_.rakutenPlan] || 0))) : 0;
  const shopifyPlatformFee = isPastMonth ? handleTax(-sum((shops || []).filter(_ => _.mall === 'shopify').map(_ => computeShopifyMonthlyFee(_.shopifyPlan, _.shopifyBillingCycle)))) : 0;
  // ★Yahoo は出店料・システム利用料が無料のため出店費用に計上しない。
  //   Yahoo のコストはすべて売上連動（ポイント原資・キャンペーン原資・アフィリエイト・決済手数料）で、
  //   computeYahooSales が values に載せているため、ここで二重に足してはいけない。
  const fixedSgaValue = amazonPlatformFee + rakutenPlatformFee + shopifyPlatformFee + otherFixedCostValue;
  const operatingProfitValue = contributionMarginValue + fixedSgaValue;

  return {
    sales: {
      label: '売上',
      computeValue: () => salesValue,
      rateHidden: true,
    },
    refund: {
      label: '売上返金',
      computeValue: () => refundValue,
      rateHidden: true,
    },
    cods: {
      label: '原価',
      computeValue: () => codsValue,
      children: {
        productCost: {
          label: '商品原価',
          computeValue: () => productCostValue,
        },
      },
    },
    grossProfit: {
      isComputed: true,
      label: '粗利',
      computeValue: () => grossProfitValue,
    },
    cods2: {
      label: '原価2',
      computeValue: () => cods2Value,
      children: {
        commission: {
          label: '販売手数料',
          computeValue: () => commissionValue,
        },
        systemUsageFee: {
          label: 'システム利用料',
          computeValue: () => systemUsageFeeValue,
        },
        safetyFee: {
          label: '安全性利用料',
          computeValue: () => safetyFeeValue,
        },
        paymentFee: {
          label: '決済手数料',
          computeValue: () => paymentFeeValue,
        },
        fbaPerUnitFulfillmentFee: {
          label: 'FBA費用',
          computeValue: () => fbaFeeValue,
        },
        shippingCost: {
          label: '配送料',
          computeValue: () => shippingCostValue,
        },
        pointsBaseCost: {
          label: 'ポイント原資',
          computeValue: () => pointsBaseCostValue,
        },
        transactionFee: {
          // 楽天の paymentFee と同階層に並ぶため、モールが分かる表記にする
          label: '決済手数料(Shopify)',
          computeValue: () => transactionFeeValue,
        },
      },
    },
    grossProfit2: {
      isComputed: true,
      label: '純粗利',
      computeValue: () => grossProfit2Value,
    },
    variableSga: {
      label: '販管費(変動費)',
      computeValue: () => variableSgaValue,
      children: {
        spAmount: {
          label: '広告費',
          computeValue: () => spAmountValue,
        },
        promotionCost: {
          label: '販売促進費',
          computeValue: () => promotionCostValue,
        },
        ...(dateRangeType === 'monthly' ? {
          inventoryFee: {
            label: '在庫保管手数料',
            computeValue: () => inventoryFeeValue,
          },
          otherVariableCost: {
            label: 'その他変動費',
            computeValue: () => otherVariableCostValue,
            dateRangeTypes: ['monthly'],
            children: mapValues(pickBy(expenseTypes, (v, k) => Math.abs(computedExpenses?.variable?.[k] || 0) > 0), (v, k) => ({
              label: expenseTypes[k]?.label,
              computeValue: () => -(computedExpenses?.variable?.[k] || 0),
            })),
          },
        } : {}),
      },
    },
    contributionMargin: {
      isComputed: true,
      label: '限界利益',
      computeValue: () => contributionMarginValue,
    },
    ...(dateRangeType === 'monthly' ? {
      fixedSga: {
        label: '販管費(固定費)',
        computeValue: () => fixedSgaValue,
        children: {
          ecPlatformFee: {
            label: '出店費用',
            computeValue: () => amazonPlatformFee + rakutenPlatformFee + shopifyPlatformFee,
          },
          otherFixedCost: {
            label: 'その他固定費',
            computeValue: () => otherFixedCostValue,
            dateRangeTypes: ['monthly'],
            children: mapValues(pickBy(expenseTypes, (v, k) => Math.abs(computedExpenses?.fixed?.[k] || 0) > 0), (v, k) => ({
              label: expenseTypes[k]?.label,
              computeValue: () => -(computedExpenses?.fixed?.[k] || 0),
            })),
          },
        },
        dateRangeTypes: ['monthly'],
      },
      operatingProfit: {
        isComputed: true,
        label: '営業利益',
        computeValue: () => operatingProfitValue,
        dateRangeTypes: ['monthly'],
      },
    } : {}),
  };
};

const computePeriodValues = (data, handleTax) => {
  const { allDailySales } = data;
  return {
    sales: handleTax(sumBy(allDailySales, 'values.sales')),
    refund: handleTax(sumBy(allDailySales, 'values.refund')),
    commission: handleTax(sumBy(allDailySales, 'values.commission')),
    systemUsageFee: handleTax(sumBy(allDailySales, 'values.systemUsageFee')),
    safetyFee: handleTax(sumBy(allDailySales, 'values.safetyFee')),
    paymentFee: handleTax(sumBy(allDailySales, 'values.paymentFee')),
    fbaFee: handleTax(sumBy(allDailySales, 'values.fbaFee')),
    pointsBaseCost: handleTax(sumBy(allDailySales, 'values.pointsBaseCost')),
    transactionFee: handleTax(sumBy(allDailySales, 'values.transactionFee')),
    pointsCost: handleTax(sumBy(allDailySales, 'values.pointsCost')),
    promotionCost: handleTax(sumBy(allDailySales, 'values.promotionCost')),
    quantity: sumBy(allDailySales, 'values.quantity'),
  };
};

const filterByDateRange = (items, startStr, endStr) => items.filter(_ => _.date >= startStr && _.date <= endStr);

// createPlItems に渡す sku→product マップ。原価は createPlItems 内で日付ごとに解決するため、
// 数値化せず product オブジェクトのまま保持する（unitCostHistory を含む）。
const buildProductsBySku = (products) => Object.fromEntries(products.map(_ => [_.sku, _]));

const computeExpensesForMonth = (expenses, monthStr, handleTax) => {
  const monthExpenses = expenses.filter(_ => _.month === monthStr);
  return mapValues(
    groupBy(monthExpenses, 'behavior'),
    _ => mapValues(
      groupBy(_, 'type'),
      _ => handleTax(sumBy(_, 'amount')),
    ),
  );
};

exports.createPlItems = createPlItems;
exports.computePeriodValues = computePeriodValues;
exports.filterByDateRange = filterByDateRange;
exports.buildProductsBySku = buildProductsBySku;
exports.computeExpensesForMonth = computeExpensesForMonth;
exports.rakutenPlanMonthlyFees = rakutenPlanMonthlyFees;
